import { useAuthStore } from '@/stores/auth';
import { TechnicianInstallationWork } from './TechnicianInstallations';
/**
 * Installation Job Detail — admin approval console (spec §8 detailed view).
 *
 * Shows all 9 verification photos with GPS overlay, checklist ticks,
 * handover signature + site details, and approve / reject / request-reupload
 * actions. On approve, surfaces the auto-generated PDF link.
 *
 * NOTE: the simple SVG map is intentionally minimal — for a prod release
 * we'd drop a Leaflet tile layer. The current SVG shows relative positions
 * and accuracy, which is enough for the ITBP demo.
 */
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '@/utils/api';
import {
  ArrowLeft, Loader2, MapPin, CheckCircle2, XCircle, AlertTriangle,
  Camera, FileDown, RotateCcw, Clock,
} from 'lucide-react';

const STEP_LABEL: Record<string, string> = {
  SERIAL: '1. Serial Number',
  WIDE_SHOT: '2. Wide Shot',
  AIR_VENT: '3. Air Vent',
  INLET: '4. Inlet',
  OUTLET: '5. Outlet',
  HANDOVER: '6. Handover',
  PLUMBING_JOINTS: '7. Plumbing Joints',
  PIPE_INSULATION: '8. Pipe Insulation',
  HEAT_TAPE: '9. Heat Tape',
};

const STEP_ORDER = ['SERIAL', 'WIDE_SHOT', 'AIR_VENT', 'INLET', 'OUTLET', 'HANDOVER', 'PLUMBING_JOINTS', 'PIPE_INSULATION', 'HEAT_TAPE'];

interface Step {
  id: string;
  stepKey: string;
  photoUrl?: string | null;
  capturedAt?: string | null;
  lat?: number | null;
  lng?: number | null;
  gpsAccuracy?: number | null;
  checklistJson?: any;
  status: string;
  reuploadReason?: string | null;
  locationDenied: boolean;
}

interface Handover {
  id: string;
  customerName: string;
  customerPhone: string;
  signatureDataUrl: string;
  signedAt: string;
  siteAddress: string;
  gpsLat?: number | null;
  gpsLng?: number | null;
  pendingWorkFlag: boolean;
  pendingWorkTypes: string[];
  otherPendingText?: string | null;
  remarks?: string | null;
}

interface JobDetail {
  id: string;
  status: string;
  serialNumber?: string | null;
  submittedAt?: string | null;
  approvedAt?: string | null;
  rejectionReason?: string | null;
  pdfReportUrl?: string | null;
  site?: {
    id: string;
    siteName: string;
    villageName: string;
    district?: string | null;
    state?: string | null;
    addressLine1?: string | null;
    pincode?: string | null;
    lat?: number | null;
    lng?: number | null;
    altitudeMeters?: number | null;
    contactName?: string | null;
    contactPhone?: string | null;
    project?: { id: string; name: string };
    salesOrder?: { id: string; number: string } | null;
  } | null;
  product?: { id: string; name: string; sku?: string | null; installationGuide?: any } | null;
  technician?: { id: string; firstName: string; lastName: string; phone?: string | null } | null;
  approvedBy?: { id: string; firstName: string; lastName: string } | null;
  steps: Step[];
  handover?: Handover | null;
}

export function InstallationJobDetailPage() { const user = useAuthStore(s=>s.user); const {id} = useParams(); return user?.role?.name === 'service_tech' ? <TechnicianInstallationWork id={id??''}/> : <InstallationJobDetailPageManager/>; }
function InstallationJobDetailPageManager() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [job, setJob] = useState<JobDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function load() {
    if (!id) return;
    setLoading(true);
    try {
      const res = await api.get(`/installation-jobs/${id}`);
      setJob(res.data?.data ?? null);
    } catch (e: any) {
      setErr(e?.response?.data?.error ?? e?.message ?? 'Load failed');
    } finally { setLoading(false); }
  }

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  async function doApprove() {
    if (!id) return;
    if (!confirm('Approve this installation and generate the PDF report?')) return;
    setBusy(true); setErr(null);
    try {
      await api.post(`/installation-jobs/${id}/approve`, { comment: '' });
      await load();
    } catch (e: any) {
      setErr(e?.response?.data?.error ?? 'Approve failed');
    } finally { setBusy(false); }
  }

  async function doReject() {
    if (!id) return;
    const reason = window.prompt('Rejection reason:');
    if (!reason || reason.length < 3) return;
    setBusy(true); setErr(null);
    try {
      await api.post(`/installation-jobs/${id}/reject`, { reason });
      await load();
    } catch (e: any) {
      setErr(e?.response?.data?.error ?? 'Reject failed');
    } finally { setBusy(false); }
  }

  async function doReupload(stepKey: string) {
    if (!id) return;
    const reason = window.prompt(`Reupload reason for ${STEP_LABEL[stepKey]}:`);
    if (!reason || reason.length < 3) return;
    setBusy(true); setErr(null);
    try {
      await api.post(`/installation-jobs/${id}/request-reupload`, { stepKey, reason });
      await load();
    } catch (e: any) {
      setErr(e?.response?.data?.error ?? 'Request failed');
    } finally { setBusy(false); }
  }

  if (loading) return <div className="p-10 text-center text-gray-400"><Loader2 className="w-5 h-5 animate-spin inline-block" /></div>;
  if (!job) return <div className="p-10 text-center text-gray-500">Not found.</div>;

  const byKey = Object.fromEntries((job.steps ?? []).map(s => [s.stepKey, s]));

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <button onClick={() => navigate(-1)} className="mb-4 inline-flex items-center gap-2 text-sm text-gray-500 hover:text-gray-800">
        <ArrowLeft className="w-4 h-4" /> Back
      </button>

      <div className="flex items-start justify-between flex-wrap gap-4 mb-5">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{job.site?.siteName ?? 'Installation Job'}</h1>
          <div className="text-sm text-gray-500 mt-1">
            <MapPin className="inline w-4 h-4 mr-1 -mt-0.5" />
            {job.site?.villageName}, {job.site?.district}, {job.site?.state}
            {job.site?.altitudeMeters ? ` · ${job.site.altitudeMeters}m alt` : ''}
          </div>
          <div className="text-sm text-gray-500 mt-1">
            {job.site?.project?.name ?? ''} · SO {job.site?.salesOrder?.number ?? '—'} · Serial <span className="font-mono">{job.serialNumber ?? '—'}</span>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {job.status === 'APPROVED' && job.pdfReportUrl && (
            <a href={job.pdfReportUrl} target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-indigo-600 text-white text-sm hover:bg-indigo-700">
              <FileDown className="w-4 h-4" /> Download PDF Report
            </a>
          )}
          {(job.status === 'SUBMITTED' || job.status === 'PARTIAL') && (
            <>
              <button disabled={busy} onClick={doApprove}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-emerald-600 text-white text-sm hover:bg-emerald-700 disabled:opacity-50">
                <CheckCircle2 className="w-4 h-4" /> Approve
              </button>
              <button disabled={busy} onClick={doReject}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-red-600 text-white text-sm hover:bg-red-700 disabled:opacity-50">
                <XCircle className="w-4 h-4" /> Reject
              </button>
            </>
          )}
        </div>
      </div>

      {err && <div className="mb-4 p-3 rounded-md bg-red-50 text-red-700 text-sm">{err}</div>}

      {/* Meta strip */}
      <div className="grid md:grid-cols-4 gap-3 mb-5">
        <Kv label="Status" value={job.status} />
        <Kv label="Technician" value={job.technician ? `${job.technician.firstName} ${job.technician.lastName}` : '—'} />
        <Kv label="Submitted" value={fmtDate(job.submittedAt)} />
        <Kv label="Approved" value={job.approvedAt ? `${fmtDate(job.approvedAt)} by ${job.approvedBy?.firstName ?? ''}` : '—'} />
      </div>

      {/* 9 step grid */}
      <div className="mb-6">
        <div className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
          <Camera className="w-4 h-4" /> 9-Step Verification
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {STEP_ORDER.map(key => {
            const s = byKey[key];
            const ok = !!s?.photoUrl && s?.status !== 'REUPLOAD_REQUESTED';
            return (
              <div key={key} className={`rounded-lg border ${s?.status === 'REUPLOAD_REQUESTED' ? 'border-amber-300' : 'border-gray-200'} bg-white overflow-hidden`}>
                <div className="px-3 py-2 flex items-center justify-between bg-gray-50 border-b border-gray-100">
                  <div className="text-sm font-medium text-gray-800">{STEP_LABEL[key]}</div>
                  {ok ? <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                       : <AlertTriangle className="w-4 h-4 text-gray-400" />}
                </div>
                <div className="aspect-video bg-gray-100 flex items-center justify-center">
                  {s?.photoUrl
                    ? <img src={s.photoUrl} className="w-full h-full object-cover" alt={key} />
                    : <div className="text-xs text-gray-400">No photo yet</div>}
                </div>
                <div className="p-3 text-xs text-gray-600 space-y-1">
                  <div className="flex items-center gap-2">
                    <Clock className="w-3 h-3" /> {fmtDate(s?.capturedAt)}
                  </div>
                  {s?.locationDenied ? (
                    <div className="text-amber-600">Location permission denied</div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <MapPin className="w-3 h-3" /> {fmtGps(s?.lat, s?.lng)} {s?.gpsAccuracy ? `(±${s.gpsAccuracy}m)` : ''}
                    </div>
                  )}
                  {s?.checklistJson && typeof s.checklistJson === 'object' && (
                    <ul className="pt-1 border-t border-gray-100 space-y-0.5">
                      {Object.entries(s.checklistJson).map(([k, v]: [string, any]) => (
                        <li key={k}>
                          <span className="text-gray-500">{k}:</span> {v === true ? '✓' : v === false ? '✗' : String(v)}
                        </li>
                      ))}
                    </ul>
                  )}
                  {s?.reuploadReason && (
                    <div className="text-amber-700">Reupload requested: {s.reuploadReason}</div>
                  )}
                  {job.status !== 'APPROVED' && s?.photoUrl && (
                    <button onClick={() => doReupload(key)} disabled={busy}
                      className="mt-1 inline-flex items-center gap-1 text-indigo-600 hover:text-indigo-800 text-xs">
                      <RotateCcw className="w-3 h-3" /> Request re-upload
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Map strip */}
      <div className="mb-6 bg-white border border-gray-200 rounded-lg p-4">
        <div className="text-sm font-semibold text-gray-700 mb-3">GPS Trail</div>
        <SvgPinMap site={job.site ?? undefined} steps={job.steps ?? []} />
      </div>

      {/* Handover + remarks */}
      {job.handover && (
        <div className="bg-white border border-gray-200 rounded-lg p-5 mb-6">
          <div className="text-sm font-semibold text-gray-700 mb-3">Customer Handover</div>
          <div className="grid md:grid-cols-2 gap-5">
            <div className="space-y-2 text-sm">
              <Kv label="Customer" value={`${job.handover.customerName} · ${job.handover.customerPhone}`} />
              <Kv label="Site Address (confirmed)" value={job.handover.siteAddress} />
              <Kv label="Signed At" value={fmtDate(job.handover.signedAt)} />
              {job.handover.pendingWorkFlag && (
                <div className="p-3 rounded-md bg-amber-50 text-amber-800 text-xs">
                  <b>Pending Work:</b> {job.handover.pendingWorkTypes.join(', ') || '—'}
                  {job.handover.otherPendingText && <div>Other: {job.handover.otherPendingText}</div>}
                  {job.handover.remarks && <div className="mt-1">Remarks: {job.handover.remarks}</div>}
                </div>
              )}
              {!job.handover.pendingWorkFlag && job.handover.remarks && (
                <Kv label="Remarks" value={job.handover.remarks} />
              )}
            </div>
            <div>
              <div className="text-xs text-gray-500 mb-1">Signature</div>
              <img src={job.handover.signatureDataUrl} className="max-h-32 border border-gray-200 rounded bg-white" alt="signature" />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Kv({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-xs text-gray-500">{label}</div>
      <div className="text-sm text-gray-900">{value}</div>
    </div>
  );
}

function fmtDate(s?: string | null) {
  if (!s) return '—';
  const d = new Date(s);
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

function fmtGps(lat?: number | null, lng?: number | null) {
  if (lat == null || lng == null) return '—';
  return `${lat.toFixed(4)}, ${lng.toFixed(4)}`;
}

// Extremely simple SVG relative-position map — good enough for demo.
function SvgPinMap({
  site, steps,
}: {
  site?: { lat?: number | null; lng?: number | null; siteName: string } | null;
  steps: Array<{ stepKey: string; lat?: number | null; lng?: number | null }>;
}) {
  const pts = steps.filter(s => s.lat != null && s.lng != null).map(s => ({ lat: s.lat!, lng: s.lng!, label: s.stepKey }));
  if (site?.lat != null && site?.lng != null) pts.push({ lat: site.lat, lng: site.lng, label: 'SITE' });
  if (pts.length === 0) return <div className="text-xs text-gray-400">No GPS data yet.</div>;
  const minLat = Math.min(...pts.map(p => p.lat));
  const maxLat = Math.max(...pts.map(p => p.lat));
  const minLng = Math.min(...pts.map(p => p.lng));
  const maxLng = Math.max(...pts.map(p => p.lng));
  const spanLat = Math.max(0.0001, maxLat - minLat);
  const spanLng = Math.max(0.0001, maxLng - minLng);

  return (
    <svg viewBox="0 0 300 180" className="w-full h-40 border border-gray-100 rounded bg-gradient-to-br from-sky-50 to-indigo-50">
      {pts.map((p, i) => {
        const x = ((p.lng - minLng) / spanLng) * 280 + 10;
        const y = 170 - ((p.lat - minLat) / spanLat) * 160;
        const isSite = p.label === 'SITE';
        return (
          <g key={i}>
            <circle cx={x} cy={y} r={isSite ? 7 : 4}
              fill={isSite ? '#4f46e5' : '#10b981'} stroke="white" strokeWidth={2} />
            <text x={x + 8} y={y + 3} fontSize="8" fill="#111">{isSite ? site!.siteName : p.label}</text>
          </g>
        );
      })}
    </svg>
  );
}
